import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Globe, Loader2, Lock, Sparkles, Save, Radar } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SAMPLE_TEXT } from "@/lib/sample";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RaceFieldHeading, RaceFieldTable } from "@/components/race/RaceField";
import { SelectionOptions } from "@/components/SelectionOptions";
import { ModelRun } from "@/components/ModelRun";

const ParsedRace = ({ race, i, checked, onToggle }) => (
  <div className={`panel p-5 transition-colors ${checked ? "border-emerald-500/40" : ""}`} data-testid={`parsed-race-${i}`}>
    <div className="flex items-start gap-4">
      <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`parsed-race-select-${i}`} className="mt-1" />
      <div className="flex-1 min-w-0">
        <RaceFieldHeading race={race} prefix={`parsed-race-${i}-heading`} />
        <div className="mt-2 text-xs num text-slate-500">
          {[race.race_name, race.state, race.date, race.track_condition, `${race.evaluation.field_size} runners`].filter(Boolean).join(" • ")}
        </div>
      </div>
    </div>
    <div className="mt-4">
      <RaceFieldTable horses={race.horses} prefix={`parsed-race-${i}-field`} />
    </div>
  </div>
);

const STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"];
const auDate = (offset) => new Date(Date.now() + offset * 86400000).toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
const DAY_OPTS = [0, 1].map((o) => ({ value: auDate(o), label: ["Today", "Tomorrow"][o] }));

const Chip = ({ on, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-3 py-1.5 rounded border text-xs font-mono transition-colors ${on ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-slate-700 text-slate-400 hover:text-white"}`}
  >
    {children}
  </button>
);

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export default function ImportCenter() {
  const [tab, setTab] = useState("scan");
  const [states, setStates] = useState(STATES);
  const [dates, setDates] = useState([DAY_OPTS[0].value, DAY_OPTS[1].value]);
  const [progress, setProgress] = useState(null);
  const [warning, setWarning] = useState(null);
  const [job, setJob] = useState(null);
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [races, setRaces] = useState(null);
  const [selected, setSelected] = useState({});
  const timer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => () => clearInterval(timer.current), []);

  const poll = (jobId) => {
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/parse/jobs/${jobId}`);
        setJob(data);
        if (data.status === "running") return setProgress(data.progress);
        clearInterval(timer.current);
        setBusy(false);
        if (data.status === "error") { setJob(null); return toast.error(data.error || "Parsing failed"); }
        setRaces(data.races);
        setWarning(data.warning);
        setSelected(Object.fromEntries(data.races.map((r, i) => [i, r.evaluation.scored])));
        toast.success(`Found ${data.races.length} race(s), ${data.races.filter((r) => r.evaluation.scored).length} to score`);
      } catch (e) {
        clearInterval(timer.current);
        setBusy(false);
        toast.error(formatApiError(e));
      }
    }, 2500);
  };

  const run = async () => {
    setBusy(true);
    setRaces(null);
    setProgress(null);
    setWarning(null);
    setJob(tab === "scan" ? { stage: "download" } : null);
    try {
      const req = { scan: () => api.post("/scan/australia", { states, dates }), text: () => api.post("/parse/text", { text }), url: () => api.post("/parse/url", { url }) };
      const { data } = await req[tab]();
      poll(data.job_id);
    } catch (e) {
      setBusy(false);
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    const chosen = races.filter((_, i) => selected[i]);
    try {
      await api.post("/races/bulk", { races: chosen, source: tab });
      toast.success(`${chosen.length} race(s) saved`);
      navigate("/program");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const visible = races ? races.map((r, i) => [r, i]).filter(([r]) => r.evaluation.scored) : [];
  const count = Object.values(selected).filter(Boolean).length;

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-2">Import Center</div>
        <h1 className="text-4xl sm:text-5xl font-bold">Bring in the field</h1>
        <p className="text-slate-400 mt-2 max-w-2xl text-sm sm:text-base">
          Paste a form guide as plain text, or scan a racing web page. AI reads it into races and runners, then your selection rules flag which races qualify.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2" data-testid="first-starter-notice">
          <Lock className="h-4 w-4 text-amber-400 shrink-0" />
          Races with first starters are never Preferred Betting Races, and first starters are never rated.
        </div>
      </div>

      <div className="grid lg:grid-cols-[400px_1fr] gap-6 items-start">
        <div className="panel p-5 lg:sticky lg:top-20">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-[#0F172A] border border-slate-800 h-auto flex-wrap justify-start w-full">
              <TabsTrigger value="scan" data-testid="import-scan-au-tab-button"><Radar className="h-4 w-4 mr-1.5" />Scan Australia</TabsTrigger>
              <TabsTrigger value="text" data-testid="import-text-tab-button"><FileText className="h-4 w-4 mr-1.5" />Paste Text</TabsTrigger>
              <TabsTrigger value="url" data-testid="import-url-tab-button"><Globe className="h-4 w-4 mr-1.5" />Scan Web Page</TabsTrigger>
            </TabsList>
            <TabsContent value="scan" className="mt-4 space-y-5">
              <SelectionOptions />
              <div>
                <div className="eyebrow mb-2">States</div>
                <div className="flex flex-wrap gap-2">
                  {STATES.map((s) => <Chip key={s} on={states.includes(s)} onClick={() => setStates(toggle(states, s))} testId={`scan-state-${s}`}>{s}</Chip>)}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-2">Race Days (AEST)</div>
                <div className="flex flex-wrap gap-2">
                  {DAY_OPTS.map((d, i) => <Chip key={d.value} on={dates.includes(d.value)} onClick={() => setDates(toggle(dates, d.value))} testId={`scan-day-${i}`}>{d.label} <span className="text-slate-500">{d.value.slice(5)}</span></Chip>)}
                </div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Downloads every published meeting on Racing Australia free fields, keeps the races that pass your options, scores every horse with your rating rules and saves the final fields.
              </p>
            </TabsContent>
            <TabsContent value="text" className="mt-4 space-y-3">
              <Textarea
                data-testid="import-text-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste race fields here: venue, race number, distance, class, track, then each runner with barrier, weight, jockey, trainer, form, career, stats and price..."
                className="min-h-[340px] num text-xs bg-[#0F172A] leading-relaxed"
              />
              <button data-testid="import-load-sample-button" onClick={() => setText(SAMPLE_TEXT)} className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                Load sample form guide
              </button>
            </TabsContent>
            <TabsContent value="url" className="mt-4 space-y-3">
              <Input data-testid="import-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.racing-site.com/form-guide/..." className="bg-[#0F172A]" />
              <p className="text-xs text-slate-500 leading-relaxed">
                The page is fetched and every race on it is read and checked against your selection rules. Some sites block automated access or load data with JavaScript; if a scan returns nothing, copy the page text and use Paste Text.
              </p>
            </TabsContent>
          </Tabs>
          <Button
            data-testid="import-scan-button"
            onClick={run}
            disabled={busy || (tab === "scan" ? !states.length || !dates.length : tab === "text" ? text.trim().length < 20 : !/^https?:\/\//.test(url))}
            className="mt-5 w-full h-11 font-semibold"
          >
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{tab === "scan" ? "Running model..." : "AI is reading the form..."}</> : <><Sparkles className="h-4 w-4 mr-2" />{{ scan: "Run My Rating Model", text: "Parse & Check Rules", url: "Scan Page for Qualifying Races" }[tab]}</>}
          </Button>
        </div>

        {tab === "scan" ? (
        <div className="panel p-5" data-testid="model-run-panel">
          <div className="eyebrow mb-4">Model Run</div>
          <ModelRun job={job} busy={busy} />
        </div>
        ) : (
        <div className="panel p-5 flex flex-col" data-testid="import-parse-preview">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow">Preferred Betting Races</div>
            {races && <span className="text-xs num text-slate-400" data-testid="import-summary">{races.length} checked • <span className="text-emerald-400">{visible.length} qualify</span></span>}
          </div>
          {busy && <div className="flex-1 grid place-items-center py-16"><div className="text-center"><div className="loader-bar mx-auto" /><p className="text-xs text-slate-500 mt-3" data-testid="import-progress">{progress ? `${progress.done}/${progress.total} meetings • ${progress.label}` : "This can take up to a minute"}</p></div></div>}
          {!busy && !races && <div className="flex-1 grid place-items-center py-16 text-sm text-slate-500 text-center">Parsed races will appear here.</div>}
          {!busy && races && (
            <>
              {warning && <div data-testid="import-warning" className="mb-3 text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2">{warning}</div>}
              <div className="space-y-4 flex-1">
                {visible.length === 0 && <p className="text-sm text-slate-500" data-testid="import-no-races">{races.length ? "None of the races checked meet your selection rules." : "No races with runners were found."}</p>}
                {visible.map(([r, i]) => (
                  <ParsedRace key={i} race={r} i={i} checked={!!selected[i]} onToggle={(v) => setSelected({ ...selected, [i]: !!v })} />
                ))}
              </div>
              <Button data-testid="import-save-race-button" onClick={save} disabled={!count} className="mt-4 h-11 font-semibold sticky bottom-4 shadow-lg shadow-black/40">
                <Save className="h-4 w-4 mr-2" />Save {count} race(s) & Calculate Ratings
              </Button>
            </>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
